import { FC } from 'react';

import { X, Info, Check } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/TailwindComponents/base/Tooltip/Tooltip';

export type ListItemVariant =
  | 'default'
  | 'info'
  | 'warning'
  | 'error'
  | 'success';

export type ListItemEventData = {
  id?: string;
  title: string;
  description?: string;
  tags?: string[];
  variant?: ListItemVariant;
  enableRemoveItem?: boolean;
  tooltipText?: string;
};

const variantClasses: Record<ListItemVariant, string> = {
  default: 'border-gray-200 bg-white',
  info: 'border-[#5586FF] bg-[#DDE7FF]',
  warning: 'border-[#FECB00] bg-[#FFF5CC]',
  error: 'border-[#EE3E5C] bg-[#FCD8DE]',
  success: 'border-[#6DCCC4] bg-[#E2F5F3]',
};

const variantIconClasses: Record<
  Exclude<ListItemVariant, 'default'>,
  string
> = {
  info: 'text-[#5586FF] bg-[#DDE7FF] cursor-pointer',
  warning: 'text-[#FECB00] bg-[#FFF5CC] cursor-pointer',
  error: 'text-[#EE3E5C] bg-[#FCD8DE] cursor-pointer',
  success: 'text-[#6DCCC4] bg-green-50 cursor-pointer border border-[#6DCCC4]',
};

const TITLE_COLOR = '#0E3145';
const DESCRIPTION_COLOR = '#566F7D';
const TAGS_COLOR = '#566F7D';

interface ListItemProps {
  id?: string;
  title: string;
  description?: string;
  tags?: string[];
  variant?: ListItemVariant;
  enableRemove?: boolean;
  tooltipText?: string;
  onRemove?: (
    e: React.MouseEvent<HTMLButtonElement>,
    data: ListItemEventData,
  ) => void;
  onSelect?: (
    e: React.MouseEvent<HTMLDivElement>,
    data: ListItemEventData,
  ) => void;
  className?: string;
}

export const ListItem: FC<ListItemProps> = ({
  id,
  title,
  description,
  tags,
  variant = 'default',
  onRemove,
  onSelect,
  className = '',
  enableRemove = true,
  tooltipText,
}) => {
  const eventData = { id, title, description, tags, variant, tooltipText };
  return (
    <div
      id={id}
      className={`flex items-center justify-between rounded-[6px] border p-[15px] ${
        variantClasses[variant]
      } ${
        onSelect ? 'cursor-pointer hover:bg-gray-50 transition' : ''
      } ${className}`}
      onClick={onSelect ? (e) => onSelect(e, eventData) : undefined}
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-base flex items-center gap-2">
          <span
            className={`text-[14px] text-[${TITLE_COLOR}] font-semibold break-words`}
          >
            {title}
          </span>
          {variant !== 'default' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`inline-flex items-center justify-center rounded-full p-1 flex-shrink-0 ${variantIconClasses[variant]}`}
                  >
                    {variant === 'success' ? (
                      <Check className="w-2 h-2" />
                    ) : (
                      <Info className="w-5 h-5" />
                    )}
                  </span>
                </TooltipTrigger>
                {tooltipText && (
                  <TooltipContent>
                    <p>{tooltipText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {description && (
          <div
            className={`text-[14px] font-[400] text-[${DESCRIPTION_COLOR}] break-words whitespace-pre-wrap`}
          >
            {description}
          </div>
        )}
        {tags && tags.length > 0 && (
          <div className="text-sm text-gray-700 mt-1 flex flex-wrap gap-1">
            {tags.map((tag, index) => (
              <span key={tag} className="flex items-center">
                <span className={`text-[${TAGS_COLOR}] break-words`}>
                  {tag}
                </span>
                {index < tags.length - 1 && (
                  <span className="text-[#EE3E5C] mx-1">·</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      {enableRemove && (
        <button
          className="text-gray-500 hover:text-red-600 text-xl px-2 flex-shrink-0"
          aria-label="Remove item"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.(e, eventData);
          }}
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

ListItem.displayName = 'ListItem';
